import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, OnChanges, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { AssistantChatMessage } from '@omni/ai/features/assistant-chat.feature';
import { AssistantAttachment } from '@omni/ai/features/assistant-attachment';
import type { CardAction, CardResponse } from '@omni/ai/features/card.schema';
import { NovaMicInputComponent } from '../../nova-mic-input/nova-mic-input.component';
import { NovaCardComponent } from '../../nova-card/nova-card.component';
import { NovaSlashPipe } from '../../nova-slash.pipe';
import { NovaMarkdownPipe } from '../../nova-markdown.pipe';
import { CardActionsComponent } from '../../nova-card/card-actions.component';
import type { NovaFeedbackEvent } from '../../nova-feedback-footer/nova-feedback-footer.component';

@Component({
  selector: 'nova-assistant-chat',
  standalone: true,
  imports: [
    CommonModule, FormsModule, IonicModule, TranslateModule,
    NovaMicInputComponent, NovaCardComponent, CardActionsComponent,
    NovaSlashPipe, NovaMarkdownPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './assistant-chat.component.html',
  styleUrls: ['./assistant-chat.component.scss'],
})
export class NovaAssistantChatComponent implements OnChanges {
  @Input() messages: AssistantChatMessage[] = [];
  @Input() isSending = false;
  /**
   * True while the host is fetching prior conversation history. Drives a
   * "Catching up…" skeleton in the empty state so the panel doesn't flash
   * the generic empty hint before history lands.
   */
  @Input() isHistoryLoading = false;
  /** True while the device has no connection; the composer cannot send. */
  @Input() isOffline = false;
  /** Attachments to display as chips on the mic-input (managed by the harness). */
  @Input() attachments: AssistantAttachment[] = [];
  /**
   * Greeting + starter pills for an empty thread, composed by the Assistant agent.
   * Null until it arrives (or when it is unavailable), in which case the empty state
   * keeps its static hint. Its `actions` reach the harness through the same
   * {@link action} output that card chips use.
   */
  @Input() welcome: CardResponse | null = null;
  /**
   * True while the welcome turn is in flight. It is a live agent call and takes
   * several seconds, so the empty state shows its headline, hint and composer at once,
   * with placeholder prompts where the agent's prompts will appear.
   */
  @Input() isWelcomeLoading = false;

  /** True while the harness is fetching more starter prompts. */
  @Input() isToppingUpWelcome = false;

  /** Raised when the rep asks for more starter prompts and the pool is running low. */
  @Output() topUpWelcome = new EventEmitter<void>();
  @Output() sendMessage = new EventEmitter<string>();
  @Output() voiceError = new EventEmitter<string>();
  /** Forwarded from the mic-input @ button — harness opens the picker. */
  @Output() attachClick = new EventEmitter<void>();
  /** Forwarded from the mic-input chip remove button — harness removes the item. */
  @Output() removeAttachment = new EventEmitter<AssistantAttachment>();
  /**
   * Re-emitted from <nova-card> when the rep taps an entity card or an action
   * button that opens a record.
   * The nova-voice parent wires this to its NavigationService call.
   * Contract: push PageName.ContactDetailsComponent [{contact_id:id}]
   * or PageName.AccountDetailsComponent [{accountId:id}].
   */
  @Output() openEntity = new EventEmitter<{ kind: 'contact' | 'account'; id: string }>();

  /**
   * Re-emitted from <nova-card> for `kind:'prompt'` action chips (card-level +
   * per-insight). The harness sends the chip's `value` as the next user turn.
   * `kind:'tool'` chips are dispatched inside the card shell and never reach here.
   */
  @Output() action = new EventEmitter<CardAction>();

  /** Re-emitted from <nova-card>'s feedback footer — a thumbs vote on an assistant turn. */
  @Output() feedback = new EventEmitter<NovaFeedbackEvent>();

  @ViewChild('thread') threadEl?: ElementRef<HTMLDivElement>;

  /** Starter prompts shown at once. The rest of the pool waits behind "More ideas". */
  static readonly WELCOME_PAGE = 4;

  /** Which slice of the pool is on screen. Advances (and wraps) on every "More ideas". */
  #welcomePage = 0;

  /** The pool the agent has composed so far, across the first turn and any top-ups. */
  get welcomePool(): CardAction[] {
    return this.welcome?.actions ?? [];
  }

  /** How many full slices the pool currently holds. */
  get #welcomePageCount(): number {
    return Math.ceil(this.welcomePool.length / NovaAssistantChatComponent.WELCOME_PAGE);
  }

  /**
   * The rep has asked for prompts the pool does not hold yet and more are on the way — show
   * placeholders rather than silently handing back a slice they have already seen.
   */
  get isAwaitingMoreIdeas(): boolean {
    return this.isToppingUpWelcome && this.#welcomePage >= this.#welcomePageCount;
  }

  /** The slice of starter prompts currently on screen. */
  get welcomeActions(): CardAction[] {
    const pool = this.welcomePool;
    const size = NovaAssistantChatComponent.WELCOME_PAGE;
    if (pool.length <= size) return pool;
    if (this.isAwaitingMoreIdeas) return [];
    // Past the end with nothing more coming (the pool is capped): start the rotation over.
    const page = this.#welcomePage % this.#welcomePageCount;
    return pool.slice(page * size, page * size + size);
  }

  /** Varied placeholder widths, so the skeleton row reads as prompts rather than a progress bar. */
  readonly skeletonWidths = [104, 88, 120, 96];

  /** "More ideas" is only worth offering once there is something else to show. */
  get hasMoreWelcomeIdeas(): boolean {
    return this.welcomePool.length > NovaAssistantChatComponent.WELCOME_PAGE;
  }

  /**
   * Show the next slice. Asks the harness to top the pool up as the rep nears its end, so
   * the fetch overlaps with them reading the prompts they were just given.
   */
  onMoreIdeas(): void {
    this.#welcomePage += 1;
    // Top up as the rep reaches the last slice, so the fetch overlaps with them reading the
    // prompts they were just given rather than starting when they have run out.
    if (this.#welcomePage + 1 >= this.#welcomePageCount) this.topUpWelcome.emit();
  }

  /** Either wait is in progress, so the agent's greeting and starter prompts are held back. */
  get isEmptyLoading(): boolean {
    return this.isHistoryLoading || this.isWelcomeLoading;
  }

  /** Placeholder chips stand where starter prompts will appear: while the welcome turn runs, and
   *  while more are fetched past the end of the pool. */
  get showsPromptPlaceholders(): boolean {
    return this.isAwaitingMoreIdeas || (this.isWelcomeLoading && !this.isHistoryLoading);
  }

  /** Translation key of the empty-state headline for whichever state the panel is in. */
  get emptyTitleKey(): string {
    return this.isHistoryLoading ? 'NOVA_CATCHING_UP' : 'NOVA_HOW_CAN_I_HELP';
  }

  /** The welcome card's greeting prose — its first `text` section. */
  get welcomeGreeting(): string {
    const section = this.welcome?.sections.find(s => s.type === 'text');
    return section?.type === 'text' ? section.text : '';
  }

  ngOnChanges(): void {
    this.#scrollToBottom();
  }

  /** Bubbles a send from the mic-input child up to whichever harness mounted us. */
  onMicSend(text: string): void {
    if (!text?.trim() || this.isSending) return;
    this.sendMessage.emit(text.trim());
  }

  /** Bubbles voice-input errors so the harness can show a toast. */
  onMicError(message: string): void {
    this.voiceError.emit(message);
  }

  /** Re-emits the entity-tap event from <nova-card> up to the nova-voice harness. */
  onOpenEntity(e: { kind: 'contact' | 'account'; id: string }): void {
    this.openEntity.emit(e);
  }

  /** Re-emits a `kind:'prompt'` action chip from <nova-card> up to the harness. */
  onAction(a: CardAction): void {
    this.action.emit(a);
  }

  /** Re-emits a thumbs vote from <nova-card>'s feedback footer up to the harness. */
  onFeedback(e: NovaFeedbackEvent): void {
    this.feedback.emit(e);
  }

  #scrollToBottom(): void {
    setTimeout(() => {
      const el = this.threadEl?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
  }
}
